import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ArrowLeft, Plus, ChevronRight } from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  usePaymentMethods,
  usePaymentMethodImageUrls,
  useReorderPaymentMethods,
  useRecurring,
  useUsdToJpyRate,
  usePaymentMethodsTab,
} from '@/lib/hooks'
import type { PaymentMethod, RecurringTransaction } from '@/lib/hooks'
import { PaymentMethodCard } from '@/components/payment-method/payment-method-card'
import { mergePreservingOrder } from '@/lib/sortable-reorder'
import { convertUsdToJpy } from '@/lib/exchange-rate'
import { formatAmount } from '@/lib/utils'

function computeMonthlyJpy(recurring: RecurringTransaction[], usdToJpyRate?: number): number {
  let jpy = 0
  let usd = 0
  for (const r of recurring) {
    if (r.frequency === 'monthly') {
      if (r.currency === 'USD') usd += r.amount
      else jpy += r.amount
    } else {
      const months = r.frequency_months?.length ?? 0
      if (months > 0) {
        const perMonth = (r.amount * months) / 12
        if (r.currency === 'USD') usd += perMonth
        else jpy += perMonth
      }
    }
  }
  if (usd > 0 && usdToJpyRate) {
    jpy += convertUsdToJpy(usd, usdToJpyRate)
  }
  return Math.round(jpy)
}

function SortableRow({
  item,
  imageUrl,
  monthlyJpy,
}: {
  item: PaymentMethod
  imageUrl: string | null
  monthlyJpy: number
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    // touch-action は意図的に未指定 (=auto)。none にするとカード上で native の縦スクロール
    // が効かなくなるため。代わりに TouchSensor の delay: 200ms + tolerance: 5px に任せて、
    // 「指が静止して 200ms 経過 → ドラッグ起動」「指を 5px 以上動かす → スクロール (dnd-kit
    // 活性化前にキャンセル)」 という棲み分けを成立させる。
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card className="py-0">
        <CardContent className="flex items-center gap-3 px-3 py-2.5">
          <PaymentMethodCard
            source={item}
            imageUrl={imageUrl}
            size="sm"
          />
          <Link
            to={`/settings/payment-methods/${item.id}`}
            className="flex flex-1 items-center gap-2 truncate min-w-0"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium">{item.name}</span>
                {item.scope === 'personal' && (
                  <Badge variant="outline" className="shrink-0 text-xs">個人</Badge>
                )}
              </div>
              {monthlyJpy > 0 && (
                <p className="text-[11px] text-muted-foreground tabular-nums">
                  月 {formatAmount(monthlyJpy)}
                </p>
              )}
            </div>
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}

function DragPreview({
  item,
  imageUrl,
  monthlyJpy,
}: {
  item: PaymentMethod
  imageUrl: string | null
  monthlyJpy: number
}) {
  return (
    <Card className="py-0 shadow-lg">
      <CardContent className="flex items-center gap-3 px-3 py-2.5">
        <PaymentMethodCard source={item} imageUrl={imageUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">{item.name}</span>
            {item.scope === 'personal' && (
              <Badge variant="outline" className="shrink-0 text-xs">個人</Badge>
            )}
          </div>
          {monthlyJpy > 0 && (
            <p className="text-[11px] text-muted-foreground tabular-nums">
              月 {formatAmount(monthlyJpy)}
            </p>
          )}
        </div>
        <span className="size-8" aria-hidden="true" />
      </CardContent>
    </Card>
  )
}

function SortableList({
  items,
  imageUrlMap,
  monthlyJpyMap,
  onReorder,
}: {
  items: PaymentMethod[]
  imageUrlMap: Record<string, string>
  monthlyJpyMap: Record<string, number>
  onReorder: (next: PaymentMethod[]) => void
}) {
  // gardenapp と同じ活性化条件: マウスは 8px 移動で起動、タッチは 200ms 長押し + 5px 寛容。
  // PointerSensor だと delay も distance も同じ閾値しか持てず、マウス/タッチを別チューニング
  // できないので MouseSensor + TouchSensor を分離。
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  )

  const ids = useMemo(() => items.map((i) => i.id), [items])
  const [draggingId, setDraggingId] = useState<string | null>(null)

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex((i) => i.id === active.id)
    const newIndex = items.findIndex((i) => i.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onReorder(arrayMove(items, oldIndex, newIndex))
  }

  const handleDragCancel = () => setDraggingId(null)

  const draggingItem = draggingId ? items.find((i) => i.id === draggingId) : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((s) => (
            <SortableRow
              key={s.id}
              item={s}
              imageUrl={s.image_path ? imageUrlMap[s.image_path] ?? null : null}
              monthlyJpy={monthlyJpyMap[s.id] ?? 0}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {draggingItem ? (
          <DragPreview
            item={draggingItem}
            imageUrl={draggingItem.image_path ? imageUrlMap[draggingItem.image_path] ?? null : null}
            monthlyJpy={monthlyJpyMap[draggingItem.id] ?? 0}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default function PaymentMethodsPage() {
  const { data, isLoading } = usePaymentMethods()
  const reorderMutation = useReorderPaymentMethods()

  // gardenapp の StatusManager と同じく、描画と DnD は完全にローカル state ベース。
  // React Query キャッシュ (data) は「初期化」と「他経路で変更があった場合の取り込み」のために
  // useEffect で mergePreservingOrder するだけ。これにより drop の瞬間に
  // React Query キャッシュ更新由来の再レンダー (= SortableContext items 参照変化) が起きず、
  // dnd-kit の drop アニメーションが滑らかに settle する。
  const [order, setOrder] = useState<PaymentMethod[]>([])

  useEffect(() => {
    if (!data) return
    setOrder((prev) => mergePreservingOrder(prev, data))
  }, [data])

  const { data: imageUrlData } = usePaymentMethodImageUrls(order)
  const imageUrlMap = useMemo(() => imageUrlData ?? {}, [imageUrlData])

  const { data: allRecurring = [] } = useRecurring('all')
  const { data: usdToJpyRate } = useUsdToJpyRate()
  const [tab, setTab] = usePaymentMethodsTab()

  const { cards, banks } = useMemo(() => ({
    cards: order.filter((s) => s.type === 'credit_card'),
    banks: order.filter((s) => s.type === 'bank_account'),
  }), [order])

  // 各 payment_method の「月間の利用料 (JPY 換算)」 を id → 値の Map で保持。
  // - クレカ: そのカードに紐付く active recurring の月額
  // - 銀行: 直接紐付く + 「この口座を引き落とし口座として指す」 カードたちに紐付く active recurring を合算
  const monthlyJpyMap = useMemo(() => {
    const activeRec = allRecurring.filter((r) => r.is_active)
    const recByMethod = new Map<string, RecurringTransaction[]>()
    for (const r of activeRec) {
      if (!r.payment_method_id) continue
      const list = recByMethod.get(r.payment_method_id) ?? []
      list.push(r)
      recByMethod.set(r.payment_method_id, list)
    }
    // bank_id → 紐付くカードたちの recurring を flatten
    const cardRecByBank = new Map<string, RecurringTransaction[]>()
    for (const m of order) {
      if (m.type !== 'credit_card' || !m.linked_bank_method_id) continue
      const cardRec = recByMethod.get(m.id) ?? []
      if (cardRec.length === 0) continue
      const existing = cardRecByBank.get(m.linked_bank_method_id) ?? []
      cardRecByBank.set(m.linked_bank_method_id, [...existing, ...cardRec])
    }

    const result: Record<string, number> = {}
    for (const m of order) {
      const direct = recByMethod.get(m.id) ?? []
      const viaCards = m.type === 'bank_account' ? (cardRecByBank.get(m.id) ?? []) : []
      result[m.id] = computeMonthlyJpy([...direct, ...viaCards], usdToJpyRate)
    }
    return result
  }, [order, allRecurring, usdToJpyRate])

  if (isLoading && order.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-6">
        <div className="mb-6 flex items-center gap-2">
          <Link to="/settings" replace>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">支払い方法</h1>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  // 同 type 内でのみ並び替え。sort_order はテーブル全体で共有のため、
  // 両 type を含めた全件の sort_order を 0..n-1 で振り直す。
  const handleReorder = (typeFilter: 'credit_card' | 'bank_account', nextWithinType: PaymentMethod[]) => {
    const other = order.filter((s) => s.type !== typeFilter)
    const merged = typeFilter === 'credit_card'
      ? [...nextWithinType, ...other]
      : [...other, ...nextWithinType]
    const reorderedWithPosition = merged.map((m, idx) => ({ ...m, sort_order: idx }))
    // ローカル state を即時更新 → このコンポーネントが新しい順序で描画
    setOrder(reorderedWithPosition)
    // サーバ書き込みは fire-and-forget (mutation 完了でキャッシュ invalidate しない)
    reorderMutation.mutate(reorderedWithPosition.map((m) => ({ id: m.id, sort_order: m.sort_order })))
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <div className="mb-4 flex items-center gap-2">
        <Link to="/settings" replace>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="size-5" />
          </Button>
        </Link>
        <h1 className="text-xl font-semibold">支払い方法</h1>
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        登録した支払い方法は、固定支出に紐付けて「どのカード・口座で月いくら払っているか」を把握できます。長押しで並び替えできます。
      </p>

      {order.length === 0 ? (
        <div className="space-y-3">
          <p className="py-8 text-center text-sm text-muted-foreground">
            まだ支払い方法が登録されていません
          </p>
          <Link
            to="/settings/payment-methods/new"
            className="flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
          >
            <Plus className="size-4" />
            追加
          </Link>
        </div>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList className="w-full">
            <TabsTrigger value="cards">クレジットカード ({cards.length})</TabsTrigger>
            <TabsTrigger value="banks">銀行口座 ({banks.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="cards" className="mt-3">
            {cards.length === 0 ? (
              <Link
                to="/settings/payment-methods/new"
                className="flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
              >
                <Plus className="size-4" />
                クレジットカードを追加
              </Link>
            ) : (
              <>
                <SortableList monthlyJpyMap={monthlyJpyMap}
                  items={cards}
                  imageUrlMap={imageUrlMap}
                  onReorder={(next) => handleReorder('credit_card', next)}
                />
                <Link
                  to="/settings/payment-methods/new"
                  className="mt-2 flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
                >
                  <Plus className="size-4" />
                  追加
                </Link>
              </>
            )}
          </TabsContent>
          <TabsContent value="banks" className="mt-3">
            {banks.length === 0 ? (
              <Link
                to="/settings/payment-methods/new"
                className="flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
              >
                <Plus className="size-4" />
                銀行口座を追加
              </Link>
            ) : (
              <>
                <SortableList monthlyJpyMap={monthlyJpyMap}
                  items={banks}
                  imageUrlMap={imageUrlMap}
                  onReorder={(next) => handleReorder('bank_account', next)}
                />
                <Link
                  to="/settings/payment-methods/new"
                  className="mt-2 flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
                >
                  <Plus className="size-4" />
                  追加
                </Link>
              </>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
